import { ApolloError } from '@apollo/client';
import { ApplicationError, ValidationError } from '.';

export const getApplicationError = (error?: ApolloError) =>
  error?.graphQLErrors?.find((e) => !!e.extensions?.applicationError)?.extensions
    ?.applicationError as ApplicationError | undefined;

export const getValidationErrors = (error?: ApolloError) =>
  error?.graphQLErrors
    ?.filter((e) => !!e.extensions?.validationError)
    ?.map((e) => e.extensions?.validationError) as ValidationError[] | undefined;

/**
 * Human-readable message for a rejected mutation/query. Only server-tagged
 * application and validation errors are relayed; anything else falls back to the
 * caller's copy. The raw GraphQL message channel carries internal diagnostics —
 * authorize-directive text and DB failures wrapped as "error executing <SQL>
 * [args: ...]" — and the server's error presenter does not strip them.
 */
export const messageFromError = (error: unknown, fallback: string): string => {
  // Duck-typed rather than `instanceof ApolloError`: an app that resolves its own
  // copy of @apollo/client gets a different class object, and the instance check
  // then rejects every real error and relays only the fallback.
  const apolloError = error as ApolloError | undefined;
  if (!Array.isArray(apolloError?.graphQLErrors)) {
    return fallback;
  }
  return (
    getApplicationError(apolloError)?.message ??
    getValidationErrors(apolloError)?.[0]?.message ??
    fallback
  );
};

/**
 * Whether a mutation that resolved actually failed.
 *
 * The clients here set errorPolicy: "all", so a rejected mutation resolves with
 * an errors array instead of throwing. A caller that only awaits it reads
 * failure as success, and clears a draft the server never stored.
 */
export const failed = (result: { errors?: readonly unknown[] | null }): boolean =>
  (result.errors?.length ?? 0) > 0;

/**
 * The message to show for such a mutation, or null when it succeeded. Relays
 * only what messageFromError above will relay, for the reason given there.
 */
export const messageOfFailure = (
  result: { errors?: readonly unknown[] | null },
  fallback: string
): string | null =>
  failed(result) ? messageFromError({ graphQLErrors: result.errors }, fallback) : null;
